/*
 * Global "prix barré" + protein10 auto-apply, both driven by a single /cart.js read.
 *
 * 1. Customer code strike (Variant A): when the customer has applied ANY percentage discount CODE
 *    (other than "protein10"), this strikes through the price on every product card across the site
 *    and shows the discounted price. The real percentage is read from the cart, so it always matches
 *    what Shopify actually applies.
 * 2. protein10 auto-apply: off the SAME cart read, protein10 is auto-applied to the cart on any page
 *    (so the saving reaches the cart/checkout) whenever the cart has items, protein10 isn't already
 *    applied, and the customer hasn't entered their own code. No extra request, no per-page cost
 *    beyond the cart read that already happens. The protein product strike itself is server-rendered
 *    (by the "Protein" product tag), so this script does not need to draw it.
 *
 * Performance notes:
 *  - The active discount state is cached in sessionStorage and applied SYNCHRONOUSLY on load (this
 *    is a `defer` script, so the DOM is already parsed). This means the struck price is painted in
 *    its final form on navigation -> no layout shift / CLS, no flicker.
 *  - On every load we still reconcile against /cart.js in the background, so a code applied via a
 *    discount link (/discount/CODE, a full page load that fires no cart event) is detected too. The
 *    cache only removes the flash; the cart read remains the source of truth.
 *  - The MutationObserver is scoped to product grids/recommendations, not the whole <body>.
 *
 * Product cards expose their base price via `price-list[data-base-price]` (in cents, full undiscounted
 * price). When a customer code is active it overrides EVERY card from this base price — including protein
 * collection/product cards (their server-rendered protein10 strike is replaced by the customer's code, and
 * restored from the original markup if the code is removed).
 */
(() => {
  const EXCLUDE = 'protein10';
  const CACHE_KEY = 'pb_state';
  const DYNAMIC_CONTAINERS = 'product-list, product-recommendations';

  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  const rate = (window.Shopify && Shopify.currency && parseFloat(Shopify.currency.rate)) || 1;
  const activeCurrency = (window.Shopify && Shopify.currency && Shopify.currency.active) || null;
  const locale = (window.Shopify && Shopify.locale) || document.documentElement.lang || undefined;

  let currentPercent = 0;
  let currentCode = '';
  let observer = null;
  let applyingProtein = false;

  function readCache() {
    try {
      const v = sessionStorage.getItem(CACHE_KEY);
      return v ? JSON.parse(v) : null;
    } catch (e) {
      return null;
    }
  }

  function writeCache() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({ p: currentPercent, c: currentCode }));
    } catch (e) { /* storage unavailable (private mode) -> just skip caching */ }
  }

  function buildLabel(percent, code) {
    const tv = window.themeVariables && window.themeVariables.settings;
    const tpl = (tv && tv.prixBarreText) || 'économisez [percent]% avec [code]';
    return tpl.replace(/\[percent\]/g, percent).replace(/\[code\]/g, String(code).toUpperCase());
  }

  function formatMoney(cents) {
    const amount = cents / 100;

    if (activeCurrency) {
      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: activeCurrency }).format(amount);
      } catch (e) { /* fall through to theme format */ }
    }

    const tv = window.themeVariables && window.themeVariables.settings;
    const fmt = (tv && (tv.currencyCodeEnabled ? tv.moneyWithCurrencyFormat : tv.moneyFormat)) || '{{amount}}';
    return fmt
      .replace(/\{\{\s*amount\s*\}\}/g, amount.toFixed(2))
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, Math.round(amount).toString());
  }

  function applyTo(priceList) {
    const base = parseInt(priceList.dataset.basePrice, 10);
    if (!base || base <= 0) return;

    if (priceList.dataset.pbOriginal == null) {
      priceList.dataset.pbOriginal = priceList.innerHTML;
    }

    const original = Math.round(base * rate);
    const discounted = Math.round(original * (100 - currentPercent) / 100);
    const unit = priceList.querySelector('unit-price');

    priceList.innerHTML =
      '<sale-price class="text-on-sale">' + formatMoney(discounted) + '</sale-price>' +
      '<compare-at-price class="text-subdued line-through">' + formatMoney(original) + '</compare-at-price>';

    if (unit) priceList.appendChild(unit);

    // On product pages, show the savings label next to the strikethrough price.
    if (priceList.dataset.pbContext === 'product' && currentCode) {
      const label = document.createElement('span');
      label.className = 'prix-barre-savings';
      label.textContent = buildLabel(currentPercent, currentCode);
      priceList.appendChild(label);
    }

    priceList.setAttribute('data-pb', '1');
  }

  function restore(priceList) {
    if (priceList.dataset.pbOriginal != null) {
      priceList.innerHTML = priceList.dataset.pbOriginal;
      priceList.removeAttribute('data-pb');
    }
  }

  function applyAll() {
    document.querySelectorAll('price-list[data-base-price]').forEach((pl) =>
      currentPercent > 0 ? applyTo(pl) : restore(pl)
    );
  }

  function toggleObserver() {
    if (currentPercent > 0 && !observer) {
      // Prefer the known dynamic grids, but if none exist yet (cards still being rendered by the theme on
      // a cold, cache-less first load) fall back to <body> so late-arriving cards are still struck. Only
      // ever runs while a discount is active, so there is no cost for shoppers without one.
      const targets = document.querySelectorAll(DYNAMIC_CONTAINERS);
      const roots = targets.length ? targets : [document.body];

      observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          for (const node of m.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches && node.matches('price-list[data-base-price]')) applyTo(node);
            if (node.querySelectorAll) node.querySelectorAll('price-list[data-base-price]').forEach(applyTo);
          }
        }
      });

      roots.forEach((t) => observer.observe(t, { childList: true, subtree: true }));
    } else if (currentPercent <= 0 && observer) {
      observer.disconnect();
      observer = null;
    }
  }

  // Apply the currently known state to the page (cheap, synchronous).
  function render() {
    applyAll();
    toggleObserver();
  }

  // Auto-apply protein10 to the cart whenever it is relevant, on ANY page. Runs off the cart read that
  // refresh() already did, so it adds no request on its own. It respects the customer: never overwrites
  // another discount code, never re-applies if already present, and does nothing on an empty cart. When it
  // does apply, it dispatches cart:refresh once so totals/drawer update (that re-read then short-circuits).
  function maybeApplyProtein(cart, codes) {
    if (applyingProtein || !cart || !cart.item_count) return;

    const titles = codes.map((d) => String(d.title || '').toLowerCase());
    const hasProtein = titles.includes(EXCLUDE);
    const hasOtherCode = titles.some((t) => t && t !== EXCLUDE);
    if (hasProtein || hasOtherCode) return;

    applyingProtein = true;
    fetch(root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ discount: EXCLUDE })
    })
      .then((r) => { if (r && r.ok) document.dispatchEvent(new CustomEvent('cart:refresh')); })
      .catch(() => { /* applied as a normal, removable cart discount; ignore transient failures */ })
      .finally(() => { applyingProtein = false; });
  }

  // Collect every discount-CODE application from the cart, whether it sits at cart level (order-wide
  // codes) or is allocated to individual line items (product-restricted codes live there). Automatic
  // discounts are skipped — they carry no code to display.
  function collectCodes(cart) {
    const out = [];
    (cart.cart_level_discount_applications || []).forEach((d) => out.push(d));
    (cart.discount_applications || []).forEach((d) => out.push(d));
    (cart.items || []).forEach((item) => {
      (item.line_level_discount_allocations || []).forEach((a) => {
        if (a && a.discount_application) out.push(a.discount_application);
      });
    });
    return out.filter((d) => d && d.type === 'discount_code');
  }

  // Read the authoritative cart state, update cache, re-render. Used on first load and cart changes.
  async function refresh() {
    let cart = null;
    let codes = [];

    try {
      cart = await (await fetch(root + 'cart.js')).json();
      codes = collectCodes(cart);

      // Strike state reacts to a customer's own percentage code, not protein10.
      const match = codes.find((d) =>
        d.value_type === 'percentage' &&
        String(d.title || '').toLowerCase() !== EXCLUDE
      );

      currentPercent = match ? parseFloat(match.value) : 0;
      currentCode = match ? (match.title || '') : '';
    } catch (e) {
      currentPercent = 0;
      currentCode = '';
    }

    writeCache();
    render();

    if (cart) maybeApplyProtein(cart, codes);
  }

  // True when the shopper just came back from checkout. The code they entered there can take a moment to
  // sync into the storefront cart, so this gates the deferred re-read below to that exact case.
  function cameFromCheckout() {
    try {
      return /checkout/i.test(document.referrer || '');
    } catch (e) {
      return false;
    }
  }

  // Re-read the cart a couple of times on idle so prix barré surfaces on its own — no action from the
  // shopper — once the checkout-entered code finishes syncing. Bounded (2 tries) and self-stopping the
  // moment a code is found, so it never becomes a polling loop. Only ever scheduled for checkout returns,
  // so ordinary browsing makes zero extra requests.
  function scheduleLateReconcile() {
    const delays = [1200, 3000];
    let i = 0;
    const tick = () => {
      if (currentPercent > 0 || i >= delays.length) return;
      const run = () => { refresh().then(tick); };
      const delay = delays[i++];
      if ('requestIdleCallback' in window) {
        setTimeout(() => requestIdleCallback(run, { timeout: 1000 }), delay);
      } else {
        setTimeout(run, delay);
      }
    };
    tick();
  }

  function init() {
    const cached = readCache();

    if (cached) {
      // Paint the last known state instantly (pre-paint on navigation -> no CLS / flash).
      currentPercent = cached.p || 0;
      currentCode = cached.c || '';
      render();
    }

    // Always reconcile with the real cart, even when we had a cache: a code entered at checkout (or via
    // a /discount/CODE link) is applied during a full page load that fires no cart event, so the only way
    // to notice it on the next storefront page is to read the cart here.
    refresh();

    // No user action needed: if we just returned from checkout, keep re-checking briefly until the code
    // syncs in, then stop. Skipped entirely for normal navigation -> no performance cost there.
    if (cameFromCheckout()) scheduleLateReconcile();

    document.addEventListener('cart:change', refresh);
    document.addEventListener('cart:refresh', refresh);
    // Re-verify when restored from the back/forward cache (e.g. returning from checkout).
    window.addEventListener('pageshow', (e) => { if (e.persisted) refresh(); });
    // Re-apply once the page is fully loaded. On a cold (cache-less) first load — e.g. a private window —
    // the theme may finish rendering product cards after our first pass, so this guarantees they get
    // struck without the shopper having to navigate/click first. Idempotent and runs at most once.
    if (document.readyState !== 'complete') {
      window.addEventListener('load', render, { once: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
